import { KOMODO } from "store/constants/destinations";
import * as Yup from "yup";

const contactInfoInitialValues = {
  title: null,
  fullname: "",
  email: "",
  code: null,
  phone: "",
  contact: [],
  wechat: "",
};

const mapContactIntialValues = (language) => {
  return { ...contactInfoInitialValues, title: language === "ja" ? "-" : null };
};

export const initialValues = (
  destination,
  subDestination,
  language,
  location
) => {
  return {
    generalInquiry: {
      subject: destination || "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    stayVoucherInquiry: {
      hotelVoucher: location === KOMODO ? ["AKWB"] : [],
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    cruiseInquiry: {
      sailingMonth: "",
      preferredRoom: destination || "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    dailyBoatTrips: {
      activity: destination || "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    meetingsEvents: {
      destination: destination || "",
      eventName: "",
      eventType: "",
      guestCount: "",
      fromDesireDate: "",
      untilDesireDate: "",
      fromAlternativeDate: "",
      untilAlternativeDate: "",
      eventRequirement: "",
      accommodationRequirement: "",
      companyName: "",
      companyAddress: "",
      countryOption: "indonesia",
      city: "",
      country: "",
      ...mapContactIntialValues(language),
    },
    wedding: {
      destination: language !== "ja" && (destination || ""),
      weddingPlan: "",
      scheduleOptions: "",
      weddingDateOption: "knownDate",
      desireDate: "",
      alternativeDate: "",
      guestCount: "",
      additionalNote: "",
      nationality: "",
      brochurePreference: "",
      postalCode: "",
      prefecture: "",
      address: "",
      addressDetail: "",
      hiraganaName: "",
      ...mapContactIntialValues(language),
    },
    boatCharter: {
      boat: "",
      purpose: "",
      sailingMonth: "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    dining: {
      restaurant: destination || "",
      requestDate: "",
      requestHour: "",
      requestTime: "",
      adult: "",
      child: "",
      hotel: "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
    spa: {
      spa: destination || "",
      spaTreatment: subDestination || "",
      requestDate: "",
      requestHour: "",
      requestTime: "",
      guests: "",
      hotel: "",
      checkinDate: "",
      inquiry: "",
      inquiryCheck: false,
      promotional: "",
      ...mapContactIntialValues(language),
    },
    experience: {
      experience: destination || "",
      requestDate: "",
      checkIndate: "",
      hotelOptions: "",
      inquiry: "",
      promotional: "",
      ...mapContactIntialValues(language),
    },
  };
};

export const jakartaInitialValues = ({ language }) => ({
  ...mapContactIntialValues(language),
  subject: "",
  inquiry: "",
});

const commonValidations = {
  title: Yup.string().nullable().required("Title is required"),
  fullname: Yup.string().required("Full Name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  code: Yup.string().nullable().required("Code is required"),
  phone: Yup.string().required("Phone number is required"),
  contact: Yup.array().min(1, "Contact is required"),
};

export const validation = {
  generalInquiry: Yup.object().shape({
    ...commonValidations,
    inquiry: Yup.string().required("Inquiry is required"),
    subject: Yup.string().required("Subject is required"),
  }),
  stayVoucherInquiry: Yup.object().shape({
    ...commonValidations,
    inquiry: Yup.string().required("Inquiry is required"),
    hotelVoucher: Yup.array().min(1, "Hotel is required"),
  }),
  cruiseInquiry: Yup.object().shape({
    ...commonValidations,
    inquiry: Yup.string().required("Inquiry is required"),
    sailingMonth: Yup.string().required("Select Prefered sailing month"),
    preferredRoom: Yup.string().required("Select Preferred Room"),
  }),
  dailyBoatTrips: Yup.object().shape({
    ...commonValidations,
    inquiry: Yup.string().required("Inquiry is required"),
    activity: Yup.string().required("Please select a trip"),
  }),
  meetingsEvents: Yup.object().shape({
    ...commonValidations,
    destination: Yup.string().required("Destination is required"),
    eventName: Yup.string().required("Event name is required"),
    eventType: Yup.string().required("Event type is required"),
    guestCount: Yup.string().required("Guest count is required"),
    fromDesireDate: Yup.string().required("Desired date is required"),
    untilDesireDate: Yup.string().required("Desired date is required"),
    fromAlternativeDate: Yup.string().required("Alternative date is required"),
    untilAlternativeDate: Yup.string().required("Alternative date is required"),
    eventRequirement: Yup.string().required("Event requirement is required"),
    accommodationRequirement: Yup.string().required(
      "Accommodation requirement is required"
    ),
    companyName: Yup.string().required("Company name is required"),
    companyAddress: Yup.string().required("Company address is required"),
    city: Yup.string().required("City is required"),
    country: Yup.string().when("countryOption", {
      is: (value) => value === "other",
      then: Yup.string().required("Country is required"),
    }),
  }),
  wedding: Yup.object().shape({
    ...commonValidations,
    destination: Yup.string().required("Destination is required"),
    weddingPlan: Yup.string().required("Wedding Plan is required"),
    scheduleOptions: Yup.string().required("Schedule is required"),
    weddingDateOption: Yup.string().required(
      "Wedding Date availability is required"
    ),
    desireDate: Yup.string().when("weddingDateOption", {
      is: (value) => value === "knownDate",
      then: Yup.string().required("Select Desired date"),
    }),
    alternativeDate: Yup.string().when("weddingDateOption", {
      is: (value) => value === "knownDate",
      then: Yup.string().required("Select Desired date"),
    }),
    guestCount: Yup.string().nullable().required("Guest Count is required"),
    nationality: Yup.string().required("Nationality is required"),
    postalCode: Yup.string().when("brochurePreferenceOption", {
      is: (value) => value === "address",
      then: Yup.string().required("Please input your postal code"),
    }),
    prefecture: Yup.string().when("brochurePreferenceOption", {
      is: (value) => value === "address",
      then: Yup.string().required("Please input your postal code"),
    }),
    address: Yup.string().when("brochurePreferenceOption", {
      is: (value) => value === "address",
      then: Yup.string().required("Please input your address"),
    }),
  }),
  boatCharter: Yup.object().shape({
    ...commonValidations,
    boat: Yup.string().required("Select preferred boat"),
    purpose: Yup.string().required("Select preferred purpose"),
    sailingMonth: Yup.string().required("Select Prefered sailing month"),
    inquiry: Yup.string().required("Inquiry is required"),
  }),
  dining: Yup.object().shape({
    ...commonValidations,
    restaurant: Yup.string().required("Select preferred restaurant"),
    requestDate: Yup.string().required("Select preferred date"),
    requestHour: Yup.string().required("Select preferred hour"),
    requestMinute: Yup.string().required("Select preferred minute"),
    adults: Yup.string().required("Select preferred adults"),
    hotel: Yup.string().required("Hotel is required"),
    inquiry: Yup.string().required("Inquiry is required"),
  }),
  spa: Yup.object().shape({
    ...commonValidations,
    spa: Yup.string().required("Select preferred spa"),
    spaTreatment: Yup.string().required("Select preferred spa treatment"),
    requestDate: Yup.string().when("inquiryCheck", {
      is: (value) => value === "false",
      then: Yup.string().required("Select preferred date"),
      otherwise: Yup.string(),
    }),
    requestHour: Yup.string().when("inquiryCheck", {
      is: (value) => value === "false",
      then: Yup.string().required("Select preferred hour"),
      otherwise: Yup.string(),
    }),
    requestMinute: Yup.string().when("inquiryCheck", {
      is: (value) => value === "false",
      then: Yup.string().required("Select preferred minute"),
      otherwise: Yup.string(),
    }),
    inquiry: Yup.string().when(
      ["requestMinute", "requestDate", "requestHour"],
      {
        is: (requestMinute, requestDate, requestHour) =>
          requestDate === undefined ||
          requestHour === undefined ||
          requestMinute === undefined,
        then: Yup.string().required("Inquiry is required"),
        otherwise: Yup.string(),
      }
    ),
    guests: Yup.string().required("Select preferred guests"),
    hotel: Yup.string().required("Hotel is required"),
  }),
  experience: Yup.object().shape({
    ...commonValidations,
    experience: Yup.string().required("Select Prefered experience"),
    requestDate: Yup.string().required("Select date"),
    checkInDate: Yup.string().required("Select check-in date"),
    hotelOptions: Yup.string().required("Hotel is required"),
    inquiry: Yup.string().required("Inquiry is required"),
  }),
};

export const jakartaValidation = Yup.object().shape({
  ...commonValidations,
  inquiry: Yup.string().required("Inquiry is required"),
  subject: Yup.string().required("Subject is required"),
});
